import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root'
})
export class SentryService {

  constructor() { }

  /**
   * Capture an exception and send it to Sentry
   */
  captureException(exception: any, context?: any): void {
    Sentry.captureException(exception, {
      extra: context
    });
  }

  /**
   * Capture a message and send it to Sentry
   */
  captureMessage(message: string, level: Sentry.SeverityLevel = 'info', context?: any): void {
    Sentry.captureMessage(message, {
      level,
      extra: context
    });
  }

  /**
   * Set user context for better error tracking
   */
  setUser(user: { id?: string; email?: string; username?: string; [key: string]: any }): void {
    Sentry.setUser(user);
  }

  /**
   * Set additional context for error tracking
   */
  setContext(name: string, context: { [key: string]: any }): void {
    Sentry.setContext(name, context);
  }

  /**
   * Set tags for better error categorization
   */
  setTag(key: string, value: string): void {
    Sentry.setTag(key, value);
  }

  /**
   * Add breadcrumb for better debugging
   */
  addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
    Sentry.addBreadcrumb(breadcrumb);
  }

  /**
   * Clear user context
   */
  clearUser(): void {
    Sentry.setUser(null);
  }

  /**
   * Start a performance span and return it for proper management
   * @param name The name of the span
   * @param operation The operation type (e.g., 'http.client', 'db.query')
   * @returns The created span that can be finished when the operation completes
   */
  startSpan(name: string, operation: string): Sentry.Span | undefined {
    return Sentry.startSpan({ name, op: operation }, (span) => {
      return span;
    });
  }

  /**
   * Start a span and automatically finish it when the provided function completes
   * @param name The name of the span
   * @param operation The operation type
   * @param fn The function to execute within the span
   * @returns The result of the function
   */
  withSpan<T>(name: string, operation: string, fn: (span?: Sentry.Span) => T): T {
    return Sentry.startSpan({ name, op: operation }, fn);
  }

  /**
   * Start an inactive span that needs to be manually finished
   * Useful when you need to track performance across async operations
   * @param name The name of the span  
   * @param operation The operation type
   * @returns The created span that must be manually finished
   */
  startInactiveSpan(name: string, operation: string): Sentry.Span | undefined {
    return Sentry.startInactiveSpan({ name, op: operation });
  }
} 